import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { OperativoService } from "../opeartivo.service";
import { GrupoYears } from "../modelos/modelo-grupo-years.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ModalAgregarNombreGruposComponent } from "./agregar-grupos/modal-agregar-nombre-grupos.component";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-nombre-grupos",
  standalone: true,
  templateUrl: "./nombre-grupos.component.html",
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatDatepickerModule,
  ],
})
export class NombreGruposComponent implements OnInit {
  nomGrupo = "";
  fecha: Date | null = null;
  minDate: Date;
  maxDate: Date;
  today = new Date();

  listaGrupos: GrupoYears[] = [];
  requestFilter = new ConsultaList<GrupoYears>();
  totalRegistros = 0;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;

  expandedGroupIndex: number | null = null;
  expandedOpportunityIndex: number | null = null;

  constructor(
    public modalDialog: MatDialog,
    private servicioOperativo: OperativoService,
    private msg: Msg,
    private cdr: ChangeDetectorRef,
  ) {
    this.minDate = new Date(this.today.getFullYear() - 1, 0, 1);
    this.maxDate = new Date(this.today.getFullYear(), 11, 31);
  }

  ngOnInit(): void {
    this.consultarGrupos(1);
  }

  consultarGrupos(page = 1, forceRefresh = false): void {
    this.currentPage = Math.max(page, 1);
    this.loading = forceRefresh || this.listaGrupos.length === 0;
    this.cdr.detectChanges();

    this.requestFilter.param1 = this.nomGrupo?.trim() ?? "";
    this.requestFilter.param2 = forceRefresh ? "REFRESH" : "";
    this.requestFilter.fecha_ini = this.fecha;
    this.requestFilter.regInicial = (this.currentPage - 1) * this.pageSize;
    this.requestFilter.regXPagina = this.pageSize;
    this.requestFilter.pagina = this.currentPage - 1;

    this.servicioOperativo
      .getGroupsGitlabHybridWithPagination(this.requestFilter)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response?.content) {
            this.totalRegistros = this.resolveTotalRegistros(response, response.content.length);
            this.listaGrupos = Array.isArray(response.content) ? [...response.content] : [];
            this.expandedGroupIndex = null;
            this.expandedOpportunityIndex = null;

            const maxPages = this.totalPages;
            if (maxPages > 0 && this.currentPage > maxPages) {
              this.consultarGrupos(maxPages);
              return;
            }
            this.cdr.detectChanges();
            return;
          }

          this.listaGrupos = [];
          this.totalRegistros = 0;
          this.msg.warningCall("Sin registros para mostrar", null, null);
        },
        error: () => {
          this.listaGrupos = [];
          this.totalRegistros = 0;
          this.msg.warningCall("Error al consultar datos", null, null);
        },
      });
  }

  clearDate(event?: Event): void {
    event?.stopPropagation();
    this.fecha = null;
    this.consultarGrupos(1);
  }

  clearNombrePro(): void {
    this.nomGrupo = "";
    this.consultarGrupos(1);
  }

  openAddGroup(idPadre: number | null = null): void {
    const dialogRef = this.modalDialog.open(ModalAgregarNombreGruposComponent, {
      disableClose: true,
      autoFocus: true,
      width: "60%",
      panelClass: "custom-modalbox",
      data: idPadre ?? 0,
    });

    dialogRef.afterClosed().subscribe((res: any) => {
      if (res === true || res?.pCodRetorno === "00000") {
        this.consultarGrupos(this.currentPage, true);
      }
    });
  }

  actualizarDesdeGitlab(): void {
    this.consultarGrupos(this.currentPage, true);
  }

  toggleGroup(index: number): void {
    this.expandedOpportunityIndex = null;
    this.expandedGroupIndex = this.expandedGroupIndex === index ? null : index;
  }

  toggleOpportunity(index: number): void {
    this.expandedOpportunityIndex = this.expandedOpportunityIndex === index ? null : index;
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultarGrupos(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultarGrupos(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get totalPages(): number {
    return this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  hasChildren(grupo: GrupoYears): boolean {
    return Array.isArray(grupo?.listaOportunidad) && grupo.listaOportunidad.length > 0;
  }

  getFormattedDate(date: Date | string | null | undefined): string {
    if (!date) {
      return "Sin fecha";
    }

    const parsedDate = typeof date === "string" ? new Date(date) : date;
    if (!(parsedDate instanceof Date) || Number.isNaN(parsedDate.getTime())) {
      return "Sin fecha";
    }

    return parsedDate.toLocaleDateString();
  }

  private resolveTotalRegistros(response: any, currentItems: number): number {
    const candidates = [
      response?.totalElements,
      response?.pTotalItems,
      response?.totalItems,
      response?.totalRegistros,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }
}
